/* Copyright (c) 2023 MOCA Systems, Inc.  All rights reserved. */
import { CommonModule } from '@angular/common';
import { Component, ChangeDetectionStrategy, inject, Inject, OnInit, OnDestroy } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import * as appStrings from '@touchplan/strings';

import { ImportTicketsStateService } from '../../services/import-tickets-state.service';
import { DialogReusableModule } from 'src/app/common/dialog-reusable/dialog-reusable.module';
import { ImportTicketsUploadStepComponent } from '../import-tickets-upload-step/import-tickets-upload-step.component';
import { SharedUtilityComponentsModule } from 'src/app/common/utility-components/shared-utility-components.module';
import { ImportTicketsService } from '../../services/import-tickets.service';
import { Observable, Subject, distinctUntilChanged, filter, map, takeUntil } from 'rxjs';
import { Plan, Role, Location, StateStep, Ticket, TicketImportErrorType, Variance } from '@touchplan/common';
import { ImportTicketValidationResultsStepComponent } from '../import-tickets-validation-results-step/import-ticket-validation-results-step.component';
import { BannerMessageHandler } from 'src/app/common/logging/banner-message-handler';
import { ImportTicketsInsertionPointStepComponent } from '../import-tickets-insertion-point-step/import-tickets-insertion-point-step.component';
import { ImportTicketsVariancesComponent } from '../import-tickets-variances-step/import-tickets-variances.component';
import { EsidDisambiguationComponent } from '../esid-disambiguation/esid-disambiguation.component';
import { ImportTicketsResultsStepComponent } from '../import-tickets-results-step/import-tickets-results-step.component';
import { TpStepperComponent } from '../tp-stepper/tp-stepper.component';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { Swimlane } from 'src/app/app-data/models/Swimlane';
import { analyticsEventsService } from 'src/app/plain-javascript/services/analytics/AnalyticsEventsService';
import { Router } from '@angular/router';
import { MatDividerModule } from '@angular/material/divider';
import { TicketCorrectionComponent } from '../ticket-correction/ticket-correction.component';

export interface IImportTicketsDialogData {
	projectId: string;
	planId: string;
	planList: Observable<Map<string, Plan>>;
	locationList: Observable<Map<string, Location>>;
	roleList: Observable<Map<string, Role>>;
	ticketList: Observable<Map<string, Ticket>>;
	varianceList: Observable<Map<string, Variance>>;
	swimlanesList: Observable<Map<string, Swimlane>>;
}

@Component({
	selector: 'import-tickets-dialog',
	standalone: true,
	templateUrl: './import-tickets-dialog.component.html',
	styleUrls: ['./import-tickets-dialog.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [ImportTicketsStateService, ImportTicketsService],
	imports: [
		CommonModule,
		MatDialogModule,
		MatIconModule,
		MatButtonModule,
		ReactiveFormsModule,
		DialogReusableModule,
		SharedUtilityComponentsModule,
		TpStepperComponent,
		ImportTicketsUploadStepComponent,
		ImportTicketsInsertionPointStepComponent,
		ImportTicketValidationResultsStepComponent,
		EsidDisambiguationComponent,
		ImportTicketsVariancesComponent,
		ImportTicketsResultsStepComponent,
		TicketCorrectionComponent,
		MatDividerModule,
	],
})
export class ImportTicketsDialogComponent implements OnInit, OnDestroy {
	private importTicketsState = inject(ImportTicketsStateService);
	private bannerMessageHandler = inject(BannerMessageHandler);
	private router = inject(Router);

	public appStrings = appStrings;
	public stateStep = StateStep;
	private shutdownSubject$ = new Subject<void>();
	public steps = [
		appStrings.IMPORT_TICKETS,
		appStrings.IMPORT_TICKETS_VALIDATE_STEP_TITLE,
		appStrings.IMPORT_TICKETS_INSERTION_POINT_STEP_TITLE,
		appStrings.IMPORT_TICKETS_RESOLVE_ESID_TITLE,
		appStrings.IMPORT_TICKET_MAP_VARIANCE_REASONS_STEP_TITLE,
		appStrings.IMPORT_TICKET_RESULTS_TAB_TITLE,
	];
	public readonly isLoading$ = this.importTicketsState.loading$;
	public readonly currentStepConfig$ = this.importTicketsState.currentStepConfig$;
	public readonly currentStep$ = this.importTicketsState.currentStep$;

	private loadRequiredData(): void {
		// formalize that the planId is _actually_ required on load
		this.importTicketsState.setProjectPlan({
			projectId: this.dialogData.projectId,
			planId: this.dialogData.planId,
		});
		this.importTicketsState.updateAllLists(this.dialogData);
	}

	private listenTicketImportLastError(): void {
		this.importTicketsState.lastError$
			.pipe(
				filter((lastError) => lastError?.errorType === TicketImportErrorType.UNRECOVERABLE),
				map((lastError) => lastError.error),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe((error) => {
				this.bannerMessageHandler.displayErrorMessage(error, appStrings.IMPORT_TICKETS_FILE_UPLOAD_ERROR);
			});
	}

	private listenStepsForAnalytics(): void {
		this.importTicketsState.currentStep$
			.pipe(
				filter((step) => step !== null || step !== undefined),
				distinctUntilChanged(),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe((step) => {
				analyticsEventsService.ticketImportStep(this.dialogData.planId, step);
			});
	}

	private listenStepConfigChange(): void {
		this.currentStepConfig$
			.pipe(
				map((stepConfig) => ({
					dialogPosition: stepConfig?.dialogPosition,
					dialogDimensions: stepConfig?.dialogDimensions,
					backdropType: stepConfig.backdropType,
				})),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe(({ dialogPosition, dialogDimensions, backdropType }) => {
				this.dialogRef.updatePosition(dialogPosition || {});
				this.dialogRef.updateSize(dialogDimensions?.width, dialogDimensions?.height);
				this.dialogRef.removePanelClass('import-tickets-dialog-backdrop-complete');
				this.dialogRef.removePanelClass('import-tickets-dialog-backdrop-partial');
				this.dialogRef.addPanelClass(`import-tickets-dialog-backdrop-${backdropType}`);
			});
	}

	protected skipDummyStep(): void {
		this.importTicketsState.nextStep({});
	}

	protected onOpenDataSourceSettings(): void {
		this.dialogRef.close();
		this.router.navigate(['settings', 'project', this.dialogData.projectId, 'data-sources']);
	}

	public ngOnInit(): void {
		this.loadRequiredData();
		this.listenTicketImportLastError();
		this.listenStepConfigChange();
		this.listenStepsForAnalytics();
	}

	public ngOnDestroy(): void {
		this.shutdownSubject$.next();
		this.shutdownSubject$.complete();
	}

	constructor(
		public dialogRef: MatDialogRef<ImportTicketsDialogComponent>,
		@Inject(MAT_DIALOG_DATA) public dialogData: IImportTicketsDialogData
	) {
		/**
		 * The system is currently configured to manage only one instance of this dialog.
		 * If multiple instances are detected, an error will be triggered, leading to the closure of the dialog.
		 */
		try {
			this.importTicketsState.registerDialogStateAPI(dialogRef.id);
		} catch (error) {
			console.log(error);
			this.dialogRef.close();
		}
		// BEGIN PLACEHOLDER REPLACE AS PART OF TP-5770
		console.log('projectId', this.dialogData.projectId, 'planId', this.dialogData.planId);
		// END PLACEHOLDER REPLACE AS PART OF TP-5770
	}
}
